import React, { useState, useEffect } from 'react';
import { Play, Clock } from 'lucide-react';
import { videosAPI } from '../services/api';

const Videos = () => {
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await videosAPI.getAll();
      setVideos(response.data);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getYoutubeEmbedUrl = (url) => {
    const videoId = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-white">Videos</h1>
      <p className="text-gray-400">Learn how to get the most out of CONNECT AI Magic with our tutorials.</p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Selected Video Player */}
          {selectedVideo && (
            <div className="bg-[#1a1a2e] rounded-xl p-4 border border-gray-800">
              <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden mb-4">
                <iframe
                  width="100%"
                  height="100%"
                  src={getYoutubeEmbedUrl(selectedVideo.youtube_url)}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <h2 className="text-white text-xl font-semibold">{selectedVideo.title}</h2>
              <p className="text-gray-400 mt-2">{selectedVideo.description}</p>
              {selectedVideo.duration && (
                <div className="flex items-center gap-2 text-gray-400 mt-2">
                  <Clock size={16} />
                  <span>{selectedVideo.duration}</span>
                </div>
              )}
            </div>
          )}

          {/* Video Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videos.map((video) => (
              <div
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className={`bg-[#1a1a2e] rounded-xl overflow-hidden border cursor-pointer transition-all ${
                  selectedVideo?.id === video.id
                    ? 'border-purple-500'
                    : 'border-gray-800 hover:border-purple-500/50'
                }`}
              >
                <div className="relative">
                  <img
                    src={video.thumbnail_url || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=300&h=200&fit=crop'}
                    alt={video.title}
                    className="w-full h-40 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <div className="w-14 h-14 rounded-full bg-purple-600 flex items-center justify-center">
                      <Play size={24} className="text-white fill-white" />
                    </div>
                  </div>
                  {video.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-white text-xs">
                      {video.duration}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-white font-semibold line-clamp-2">{video.title}</h3>
                  <p className="text-gray-400 text-sm mt-1 line-clamp-2">{video.description}</p>
                </div>
              </div>
            ))}
          </div>

          {videos.length === 0 && (
            <div className="text-center py-12">
              <Play size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No videos available yet.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Videos;
